import { dependentKeyCompat } from '@ember/object/compat';
import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import { RI_DEFAULT_DATE_FORMAT } from 'lumen/utils/constants/general-constants';
import {
  CANCELLATIONS_VIEWS,
  type CancellationsView,
  CAPACITY_MAX_DAYS,
  type Kpi,
  KPI_CATEGORIES,
  type KpiCategory,
  KPIS,
  type View,
  VIEWS,
} from 'lumen/utils/constants/ri-constant-values';
import getDateRange from 'lumen/utils/revenue-insight/get-date-range';
import {
  isAPIReference,
  isSameOrAfterExtractDate,
} from 'lumen/utils/revenue-insight/ri-utils';

interface RiQueryParams {
  cancellationsView?: CancellationsView;
  extractDate?: string | null;
  fromDate?: string | null;
  hidePickupLY?: string;
  kpi?: Kpi;
  kpiCategory?: KpiCategory;
  refFromDate?: string | null;
  reference?: string;
  segmentationFilters?: string;
  selectedView?: View;
  showPickupRealised?: string;
  stayDateFilter?: string;
  toDate?: string | null;
}

export default class RiSelectionsService extends Service {
  @service declare access: Services['access'];
  @service declare currency: Services['currency'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  @tracked availableKpiCategories: KpiCategory[] = [];
  @tracked isMultiProperty = false;
  @tracked isNetComparison = false;
  // True for a pace-only refetch (set by the route's refetchPace), false for a
  // model-wide load; drives the pace card's loader vs stale-while-revalidate.
  @tracked isPaceOnlyLoad = false;
  @tracked summaryOverwrite: unknown[] | null | undefined = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get activeFiltersWithoutHotels() {
    return Object.keys(this.parsedSegmentationFilters).filter(
      (segmentationId) => segmentationId !== RI_BREAKDOWNS.SUBSCRIPTION_ID,
    );
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bobKey() {
    if (this.isCancellationsPercentageView) {
      return 'cancellationBOBOrFilteredPercentage';
    }
    if (this.isCancellationsOnlyView) {
      return 'cancellationBOBOrFiltered';
    }
    return 'BOB';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bobSortKey() {
    if (this.isCancellationsOnlyView) {
      return 'cancellationBOBOrFiltered';
    }
    return 'BOB';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get budgetText() {
    return this.intl.t('revenue_insight.budget');
  }

  get cancellationsView(): CancellationsView {
    return this.queryParams.cancellationsView ?? CANCELLATIONS_VIEWS.ABSOLUTE;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get changeKey() {
    if (this.isCancellationsPercentageView) {
      return 'cancellationOrFilteredChangePercentage';
    }
    if (this.isCancellationsOnlyView) {
      return 'cancellationOrFilteredChange';
    }
    return 'change';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentCancellationsPeriodText() {
    if (this.isCustomActive) {
      return this.intl.t('revenue_insight.legend.cancellations_current');
    }
    return this.intl.t('revenue_insight.legend.cancellations_this_year');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentPeriodText() {
    if (this.isCustomActive) {
      return this.intl.t('revenue_insight.expand_view.current');
    }
    return this.intl.t('revenue_insight.this_year');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentRoute() {
    return this.isMultiProperty
      ? 'market.revenue-insight'
      : 'hotel.revenue-insight';
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dateRange() {
    const { dateRangeStart, dateRangeEnd } = getDateRange(
      this.fromDate,
      this.toDate,
      this.hotelAndMarketService,
      this.stayDateFilter,
    );
    const startDate = this.hotelAndMarketService.toLocalTime(dateRangeStart);
    const endDate = this.hotelAndMarketService.toLocalTime(dateRangeEnd);

    return {
      startDate,
      endDate,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get defaultExtractDate() {
    return (
      this.extractDate ||
      this.revenueInsightDemoFreezeDate ||
      this.hotelAndMarketService.getLocalTime().format('YYYY-MM-DD')
    );
  }

  get extractDate(): string | null {
    return this.queryParams.extractDate ?? null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get forecastText() {
    return this.intl.t('general.forecast');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedIntegrationDate() {
    const integrationDate = dayjs(
      this.hotelAndMarketService.hotel?.dataContinuityIntegrationDate,
    );

    return integrationDate.format(RI_DEFAULT_DATE_FORMAT);
  }

  get fromDate(): string | null {
    return this.queryParams.fromDate ?? null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasCancellationLeadtimeFilter() {
    return (
      Object.keys(this.parsedSegmentationFilters).filter(
        (segmentationId) =>
          segmentationId === RI_BREAKDOWNS.CANCELLATION_LEAD_TIME,
      ).length > 0
    );
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasCancellations() {
    return (
      this.access.hasFeature('revenueinsight.cancellations') &&
      this.isSingleProperty &&
      !this.isAdrSelected &&
      !this.isTargetActive
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasFiltersOtherThanHotel() {
    return (
      this.stayDateFilter.length > 0 ||
      this.activeFiltersWithoutHotels.length > 0
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHotelFilter() {
    return (
      Object.keys(this.parsedSegmentationFilters).filter(
        (segmentationId) => segmentationId === RI_BREAKDOWNS.SUBSCRIPTION_ID,
      ).length > 0
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasSummaryOverwrite() {
    return (
      this.summaryOverwrite !== null && this.summaryOverwrite !== undefined
    );
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isAdrSelected() {
    return this.kpi === KPIS.ADR;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBookingsOnlyView() {
    return this.selectedView === VIEWS.NET;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBudgetActive() {
    return this.reference === 'budget';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCancellationsAbsoluteView() {
    return this.cancellationsView === CANCELLATIONS_VIEWS.ABSOLUTE;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCancellationsOnlyView() {
    return this.selectedView === VIEWS.CANCELLATIONS;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCancellationsPercentageView() {
    return this.cancellationsView === CANCELLATIONS_VIEWS.PERCENTAGE;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCapacityEnabled() {
    const { startDate, endDate } = this.dateRange;

    // Only show capacity if we haven't gone over the amount of days the API can handle
    const isLessDaysThanMaxDays =
      startDate.diff(endDate, 'day') + 1 <= CAPACITY_MAX_DAYS;
    const selectedKpiSupportsInventory =
      this.kpi === KPIS.NR_OF_ROOMS || this.kpi === KPIS.OCCUPANCY;

    if (this.isCancellationsOnlyView) {
      return (
        this.isCancellationsAbsoluteView &&
        isLessDaysThanMaxDays &&
        selectedKpiSupportsInventory
      );
    }

    return isLessDaysThanMaxDays && selectedKpiSupportsInventory;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCustomActive() {
    if (!this.reference) {
      return false;
    }

    return !isAPIReference(this.reference);
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isForecastActive() {
    return this.reference === 'forecast';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isKPIsAndStayDateSummaryData() {
    return this.summaryOverwrite?.length === 2;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isKPIsAndStayDateSummaryVSReservationData() {
    return this.summaryOverwrite?.length === 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLastYearActive() {
    return !this.isBudgetActive && !this.isForecastActive;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOccupancySelected() {
    return this.kpi === KPIS.OCCUPANCY;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSingleProperty() {
    return !this.isMultiProperty;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTargetActive() {
    return this.isBudgetActive || this.isForecastActive;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isToDateInFuture() {
    // Use dateRange.endDate as fallback when toDate QP is null (default).
    // The controller computes a default end-of-month date that getDateRange
    // also produces, so this stays consistent with the resolved date range.
    const resolvedToDate =
      this.toDate ?? this.dateRange.endDate.format('YYYY-MM-DD');

    const extractDate =
      typeof this.defaultExtractDate === 'string'
        ? this.defaultExtractDate
        : this.defaultExtractDate.toDate();

    if (this.parsedStayDateFilter) {
      return isSameOrAfterExtractDate(
        this.parsedStayDateFilter?.endDate,
        extractDate,
      );
    }
    return isSameOrAfterExtractDate(resolvedToDate, extractDate);
  }

  get kpi(): Kpi {
    return this.queryParams.kpi ?? KPIS.OCCUPANCY;
  }

  get kpiCategory(): KpiCategory {
    return this.queryParams.kpiCategory ?? KPI_CATEGORIES.TOTAL;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get leftToPickupText() {
    if (this.isCustomActive) {
      return this.intl.t('revenue_insight.left_to_pickup_ref');
    }

    return this.intl.t('revenue_insight.left_to_pickup_ly');
  }

  @dependentKeyCompat
  get parsedSegmentationFilters(): Record<string, string[]> {
    if (this.segmentationFilters) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return JSON.parse(this.segmentationFilters);
    }
    return {};
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parsedStayDateFilter() {
    if (this.stayDateFilter) {
      /*
      Example stayDateFilter:
        stayDateFilter={"impact_date":["2018-04-11:2018-04-16"]}

      Part before the ':'' is start date
      the part after is the end date
       */
      const stayDateFilterObj = JSON.parse(this.stayDateFilter) as Record<
        string,
        string[]
      >;
      const impactDateFilter = stayDateFilterObj[RI_BREAKDOWNS.IMPACT_DATE];

      if (!impactDateFilter || !impactDateFilter[0]) {
        return null;
      }

      const endDate = impactDateFilter[0].split(':')[1];
      const startDate = impactDateFilter[0].split(':')[0];

      if (!startDate || !endDate) {
        return null;
      }

      return {
        startDate,
        endDate,
      };
    }

    return null;
  }

  get queryParams(): RiQueryParams {
    return this.router.currentRoute?.queryParams ?? {};
  }

  get reference(): string {
    return this.queryParams.reference ?? 'SWLY';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referenceCancellationsPeriodText() {
    if (this.isCustomActive) {
      return this.intl.t('revenue_insight.legend.cancellations_reference');
    }
    return this.intl.t('revenue_insight.legend.cancellations_last_year');
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referenceFinalText() {
    if (this.isCustomActive) {
      return this.intl.t('revenue_insight.budget.ref_period_final');
    }
    return this.intl.t('revenue_insight.last_year_final');
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referenceKey() {
    if (this.isCancellationsPercentageView) {
      return 'cancellationReferenceOrFilteredPercentage';
    }
    if (this.isCancellationsOnlyView) {
      return 'cancellationReferenceOrFiltered';
    }
    return 'reference';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referencePeriodNegativeChangeText() {
    if (this.isCustomActive) {
      if (this.isNetComparison) {
        return this.intl.t('revenue_insight.legend.net_negative_change_custom');
      }
      return this.intl.t('revenue_insight.legend.negative_change_custom');
    }

    if (this.isNetComparison) {
      return this.intl.t('revenue_insight.legend.net_negative_change');
    }
    return this.intl.t('revenue_insight.legend.negative_change');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referencePeriodPositiveChangeText() {
    if (this.isCustomActive) {
      if (this.isNetComparison) {
        return this.intl.t('revenue_insight.legend.net_positive_change_custom');
      }
      return this.intl.t('revenue_insight.legend.positive_change_custom');
    }

    if (this.isNetComparison) {
      return this.intl.t('revenue_insight.legend.net_positive_change');
    }
    return this.intl.t('revenue_insight.legend.positive_change');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referencePeriodText() {
    return this.getReferencePeriodText();
  }

  @dependentKeyCompat
  get refFromDate(): string | null {
    return this.queryParams.refFromDate ?? null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get revenueInsightDemoFreezeDate() {
    return this.hotelAndMarketService.hotel?.revenueInsightDemoFreezeDate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get riCurrency() {
    return (
      this.currency.selectedCurrency ??
      this.hotelAndMarketService.hotelOrMarket.revenueInsightCurrency
    );
  }

  get segmentationFilters(): string {
    return this.queryParams.segmentationFilters ?? '';
  }

  get selectedView(): View {
    return this.queryParams.selectedView ?? VIEWS.NET;
  }

  // Whether to show pickup-LY (used by charts/tooltips/breakdown). Non-target uses
  // showPickupRealised; market/target use the legacy hidePickupLY.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPickupLY() {
    return this.isMultiProperty || this.isTargetActive
      ? this.queryParams.hidePickupLY !== 'true'
      : this.showPickupRealised;
  }

  get showPickupRealised(): boolean {
    return this.queryParams.showPickupRealised === 'true';
  }

  get stayDateFilter(): string {
    return this.queryParams.stayDateFilter ?? '';
  }

  get toDate(): string | null {
    return this.queryParams.toDate ?? null;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get valuesToCalculate() {
    const values: (
      | 'value'
      | 'budgetValue'
      | 'forecastValue'
      | 'referenceValue'
      | 'cancellationValue'
      | 'cancellationReferenceValue'
      | 'forecastReference'
    )[] = ['value'];

    if (this.isBudgetActive) {
      values.push('budgetValue');
    }

    if (this.isForecastActive) {
      values.push('forecastValue');
    }

    if (this.isLastYearActive) {
      values.push('referenceValue');
      values.push('cancellationValue');
      values.push('cancellationReferenceValue');
    }

    // Best effort check to see if we need to add the forecast
    if (this.isTargetActive && this.isToDateInFuture) {
      values.push('forecastReference');
    }

    return values;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get vsBudgetText() {
    return this.intl.t('revenue_insight.vs_budget');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get vsForecastText() {
    return this.intl.t('revenue_insight.vs_forecast');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get vsReferencePeriodText() {
    return this.getVsReferencePeriodText();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get vsText() {
    return this.getVsText();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getReferencePeriodText(ignoreNetComparison = false) {
    if (this.isCustomActive) {
      if (!ignoreNetComparison && this.isNetComparison) {
        return this.intl.t('revenue_insight.net_ref_period');
      }
      return this.intl.t('revenue_insight.ref_period');
    }

    if (!ignoreNetComparison && this.isNetComparison) {
      return this.intl.t('revenue_insight.net_last_year');
    }

    return this.intl.t('revenue_insight.last_year');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getVsReferencePeriodText(ignoreNetComparison = false) {
    return this.intl.t('general.versus.option', {
      option: this.getReferencePeriodText(ignoreNetComparison),
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getVsText(ignoreNetComparison = false) {
    if (this.isBudgetActive) {
      return this.vsBudgetText;
    }

    if (this.isForecastActive) {
      return this.vsForecastText;
    }

    return this.getVsReferencePeriodText(ignoreNetComparison);
  }
}

declare module '@ember/service' {
  interface Registry {
    'ri-selections': RiSelectionsService;
  }
}
